import { datadogRum } from '@datadog/browser-rum';
import { runGuarded } from './runGuarded';
import { recordWAFTelemetry } from './telemetry';
import { MAX_WAF_REPLAYS, wafChallengeManager } from './WAFChallengeManager';
import { clearWAFTerminalFailureIfUnchanged, getWAFFailureSnapshot, publishWAFTerminalFailure } from './failureStore';
import {
  broadcastCleared,
  clearClearedHint,
  initWAFCrossTabChannel,
  isCrossTabRecoveryGateOpen,
  writeClearedHint,
} from './crossTab';
import { isCircuitCountableFailure, wafCircuitBreaker } from './circuitBreaker';
import { raceAbort, throwIfAborted } from './raceAbort';
import {
  WAFClearanceDetail,
  WAFClearanceResolveState,
  WAFClearanceRole,
  WAFRecoveryTransport,
  WAFTelemetryOutcomeExtra,
  WAFTelemetryPhase,
  type WAFProvider,
} from './types';

// Latched once per session: a challenge arrived that recovery would have handled but for a missing
// sitekey, so record the misconfig once to keep "misconfigured" observably distinct from "idle" on the
// dashboard. Shared across transports, so a session records it at most once regardless of which transport
// first met the sitekey-less challenge.
let hasRecordedMissingSitekeyMisconfig = false;
export const TestOnlyResetMissingSitekeyMisconfigLatch = (): void => {
  hasRecordedMissingSitekeyMisconfig = false;
};

/**
 * The recovery-gate predicates the caller supplies. These are passed in (rather than imported directly)
 * only to break an import cycle: they read `shouldSendCredentials`, which lives in `axiosConfig`, and
 * `axiosConfig` imports this module. Both callers pass the same shared `wafRecoveryGates`.
 */
export interface WAFRecoveryGates {
  /** Session credentials are not suspended AND a sitekey is provisioned. */
  isRecoveryEnabled: () => boolean;
  /** Credentials are sendable but no sitekey — a misconfig, not a disabled feature. */
  isBlockedOnlyByMissingSitekey: () => boolean;
}

/**
 * The result of one replay attempt, normalized across transports (axios rejects a non-2xx, fetch resolves
 * it), so `Recovered` telemetry fires only for a genuine success on either:
 * - `succeeded`: the challenge cleared and the replay is a success → emit `Recovered`, return the value.
 * - `clearedButNotOk`: the challenge cleared but the replay is not a success (a fetch non-ok response) →
 *   return the value so the caller surfaces it, but do NOT record a recovery. Emitting a terminal event
 *   here would count an origin 5xx as a WAF failure in the terminal-failure ratio, so it stays silent.
 * - `rechallenged`: still challenged → solve and replay again.
 * A genuine failure (a network error, or an axios non-2xx that rejects) is thrown out of `attempt()` and
 * propagates from `recoverWAFChallenge`; no terminal event is recorded (matching the axios path).
 */
export type WAFAttemptOutcome<T> =
  { kind: 'succeeded'; value: T } | { kind: 'clearedButNotOk'; value: T } | { kind: 'rechallenged' };

interface WAFRecoveryRequest<T> {
  provider: WAFProvider;
  transport: WAFRecoveryTransport;
  isLoginFlowRequest: boolean;
  /** performance.now() stamped by the caller at first detection, so the latency vital spans the clearance solve. */
  detectedAtMs: number;
  gates: WAFRecoveryGates;
  /** Re-issue the request under the freshly minted clearance. MUST NOT itself re-enter recovery. */
  attempt: () => Promise<WAFAttemptOutcome<T>>;
  /** Abandons the clearance wait when a stopped ai-chat send gives up; omitted for axios. */
  signal?: AbortSignal;
  /**
   * ai-chat only: re-validated after the solve. Its frozen credentialed init cannot be re-stripped at
   * dispatch, so a superseded credential session (a logout, or a logout→sign-in that re-arms credentials
   * but bumps the generation) must abandon the replay. Omitted by axios, whose interceptor re-entry
   * re-derives the bearer and strips cookies on a generation mismatch.
   */
  isSessionStillCurrent?: () => boolean;
}

/**
 * `replayed`: the request was re-issued and the value is the replay's response, hand it back to the caller
 * (a genuine recovery, or a challenge-cleared-but-non-ok response). `surrendered`: the caller surfaces the
 * original challenge unchanged.
 */
type WAFRecoveryResult<T> = { kind: 'replayed'; value: T } | { kind: 'surrendered' };

interface RecoveryVitalFields {
  durationMs: number | undefined;
  replayCount: number;
  sequenceId: number;
  clearanceRole: WAFClearanceRole | undefined;
  isLoginFlowRequest: boolean;
  transport: WAFRecoveryTransport;
}

const recordRecoveryVital = ({
  durationMs,
  replayCount,
  sequenceId,
  clearanceRole,
  isLoginFlowRequest,
  transport,
}: RecoveryVitalFields): void => {
  if (durationMs === undefined) {
    return;
  }
  const duration = durationMs;
  runGuarded(() =>
    datadogRum.addDurationVital('waf_challenge_recovery', {
      startTime: Date.now() - duration,
      duration,
      context: {
        replayCount,
        sequenceId,
        isLoginFlowRequest,
        transport,
        ...(clearanceRole !== undefined ? { clearanceRole } : {}),
      },
    }),
  );
};

/**
 * The single WAF challenge-recovery loop shared by every transport: gate, then solve the invisible
 * clearance and replay until the challenge clears, the replay cap is hit, or the solve fails.
 * The caller owns detection and the credentialed gate (both transport-specific) and passes the already
 * detected challenge in; this owns the loop, the replay counter, and all recovery telemetry.
 */
export async function recoverWAFChallenge<T>(request: WAFRecoveryRequest<T>): Promise<WAFRecoveryResult<T>> {
  const { provider, transport, isLoginFlowRequest, detectedAtMs, gates, attempt, signal, isSessionStillCurrent } =
    request;

  throwIfAborted(signal);
  if (!gates.isRecoveryEnabled()) {
    if (!hasRecordedMissingSitekeyMisconfig && gates.isBlockedOnlyByMissingSitekey()) {
      hasRecordedMissingSitekeyMisconfig = true;
      recordWAFTelemetry({
        phase: WAFTelemetryPhase.Detected,
        outcome: WAFClearanceResolveState.Unavailable,
        detail: WAFClearanceDetail.MissingSitekey,
        transport,
        isLoginFlowRequest,
      });
    }
    return { kind: 'surrendered' };
  }

  // A broadcast landing before this tab opens the channel is covered by the durable hint read inside
  // requestClearance().
  initWAFCrossTabChannel(() => wafChallengeManager.settleClearedBySibling());

  let replayCount = 0;
  // Raised once (rather than resetting the counter) on a cross-tab clear, so `replayCount` stays a monotonic
  // attempt count for telemetry.
  let replayBudget = MAX_WAF_REPLAYS;
  let lastClearanceRole: WAFClearanceRole | undefined;

  // Tab-wide guard, distinct from the per-request replay bound below: once recovery keeps failing across this
  // tab's requests, skip the solve entirely and surface the failure so a persistently-broken edge stops
  // spinning widgets.
  if (isCrossTabRecoveryGateOpen() && wafCircuitBreaker.isTripped()) {
    // Pair the terminal with a detection event like every other round, so challenge_rate does not under-count
    // during an outage that keeps the circuit open. No clearanceRole: no clearance is requested, so this must not
    // count as a started solve.
    recordWAFTelemetry({
      phase: WAFTelemetryPhase.Detected,
      outcome: WAFTelemetryOutcomeExtra.Challenge,
      wafProvider: provider,
      replayCount,
      transport,
      isLoginFlowRequest,
    });
    recordWAFTelemetry({
      phase: WAFTelemetryPhase.Terminal,
      outcome: WAFTelemetryOutcomeExtra.RecoveryCircuitOpen,
      wafProvider: provider,
      replayCount,
      transport,
      isLoginFlowRequest,
    });
    publishWAFTerminalFailure(WAFTelemetryOutcomeExtra.RecoveryCircuitOpen);
    return { kind: 'surrendered' };
  }

  for (;;) {
    // Bail before a Detected event or a clearance mint if the send was stopped between rounds. A round can
    // emit a Detected without a matching Terminal — a stop or a gate-close during the solve below, or a
    // `clearedButNotOk` replay — all accepted, rare, and harmless to the terminal-over-terminal ratio.
    throwIfAborted(signal);
    if (replayCount >= replayBudget) {
      recordWAFTelemetry({
        phase: WAFTelemetryPhase.Terminal,
        outcome: WAFTelemetryOutcomeExtra.ReplaysExhausted,
        wafProvider: provider,
        replayCount,
        clearanceRole: lastClearanceRole,
        transport,
        isLoginFlowRequest,
      });
      publishWAFTerminalFailure(WAFTelemetryOutcomeExtra.ReplaysExhausted);
      // The edge minted clearances but kept re-challenging every replay until the budget ran out: a recovery
      // failure the breaker counts so a persistently-re-challenging edge eventually trips it. Gated to the solver
      // so coalesced waiters riding the same clearances don't multiply one degradation into several counts.
      if (isCrossTabRecoveryGateOpen() && lastClearanceRole === WAFClearanceRole.Solver) {
        wafCircuitBreaker.recordFailure();
      }
      return { kind: 'surrendered' };
    }

    // No await between this read and requestClearance(), so both see the same in-flight clearance.
    const clearanceRole = wafChallengeManager.hasActiveClearance() ? WAFClearanceRole.Waiter : WAFClearanceRole.Solver;
    lastClearanceRole = clearanceRole;
    recordWAFTelemetry({
      phase: WAFTelemetryPhase.Detected,
      outcome: WAFTelemetryOutcomeExtra.Challenge,
      wafProvider: provider,
      replayCount,
      clearanceRole,
      transport,
      isLoginFlowRequest,
    });
    const result = await raceAbort(wafChallengeManager.requestClearance(), signal);
    if (result.state !== WAFClearanceResolveState.Solved) {
      // No production path resolves requestClearance() with `Cancelled` — the provider's cancelClearance()
      // teardown only runs after the manager has already settled — but a provider that did would be reporting a
      // teardown, not a failure to clear, so keep `Cancelled` out of phase:terminal defensively either way.
      if (result.detail !== WAFClearanceDetail.Cancelled) {
        recordWAFTelemetry({
          phase: WAFTelemetryPhase.Terminal,
          outcome: result.state,
          wafProvider: provider,
          replayCount,
          clearanceRole,
          detail: result.detail,
          transport,
          isLoginFlowRequest,
        });
        publishWAFTerminalFailure(result.state, result.detail);
        if (
          isCrossTabRecoveryGateOpen() &&
          clearanceRole === WAFClearanceRole.Solver &&
          isCircuitCountableFailure(result.detail)
        ) {
          wafCircuitBreaker.recordFailure();
        }
      }
      return { kind: 'surrendered' };
    }
    const clearanceWasClearedBySibling = result.detail === WAFClearanceDetail.ClearedBySibling;

    // Re-read eligibility after the solve (which has no time bound): a logout, or a logout→sign-in that
    // supersedes the credential session mid-solve must abandon the replay.
    // gates covers the sitekey and credential suspension; isSessionStillCurrent (ai-chat only) adds the generation
    // check a re-armed session would otherwise slip past. The ai-chat caller re-issues a FROZEN credentialed
    // request with no dispatch-time re-strip; the axios interceptor's re-entry re-strips, so it omits it.
    if (!gates.isRecoveryEnabled() || isSessionStillCurrent?.() === false) {
      recordWAFTelemetry({
        phase: WAFTelemetryPhase.Detected,
        outcome: WAFTelemetryOutcomeExtra.GateClosed,
        wafProvider: provider,
        replayCount,
        clearanceRole,
        transport,
        isLoginFlowRequest,
      });
      return { kind: 'surrendered' };
    }

    const replayCountWithThisAttempt = replayCount + 1;
    const failurePendingBeforeAttempt = getWAFFailureSnapshot();
    let attemptOutcome: WAFAttemptOutcome<T>;
    try {
      attemptOutcome = await attempt();
    } catch (replayError) {
      // A thrown replay is ambiguous edge evidence: an axios origin-5xx reached the edge, but offline/DNS/timeout/
      // cancel did not. Re-arm the interactive-preemption guard anyway (a wrong re-arm is cheap and self-correcting)
      // but do NOT close the breaker or tell sibling tabs — only an actual response, handled below, validates the
      // clearance.
      if (isCrossTabRecoveryGateOpen()) {
        wafChallengeManager.notifyClearanceValidated();
      }
      throw replayError;
    }
    if (attemptOutcome.kind === 'rechallenged') {
      const crossTabGateOpen = isCrossTabRecoveryGateOpen();
      // The cookie is proven bad for the whole zone, so clear the shared hint; the next round then re-solves
      // instead of riding the same bad hint again.
      if (crossTabGateOpen) {
        clearClearedHint();
      }
      // A sibling's solve is a genuine fresh start, so forgive the re-challenges this tab already burned by
      // granting the budget once more (never on the first attempt: nothing burned yet). The
      // `replayBudget === MAX_WAF_REPLAYS` guard makes it fire at most once per recovery call — without it a
      // storm of sibling clears would re-grant every round and spin solve↔re-challenge forever.
      if (clearanceWasClearedBySibling && crossTabGateOpen && replayCount > 0 && replayBudget === MAX_WAF_REPLAYS) {
        replayBudget = replayCountWithThisAttempt + MAX_WAF_REPLAYS;
      }
      replayCount = replayCountWithThisAttempt;
      continue;
    }
    // Past the rechallenge check the challenge has cleared (succeeded or clearedButNotOk), so supersede the
    // stale terminal-failure modal an earlier request left pending — WAF is working again for this transport.
    // Only the failure we saw BEFORE this attempt: one a concurrent recovery loop published during the attempt
    // is a different reference and must survive.
    clearWAFTerminalFailureIfUnchanged(failurePendingBeforeAttempt);
    const thisTabGenuinelySolved = clearanceRole === WAFClearanceRole.Solver && !clearanceWasClearedBySibling;
    // Re-arm one cross-tab interactive preemption and close the breaker off the *validated* replay (not the bare
    // solve): an edge that mints clearances but never honors them takes the re-challenge branch above and never
    // reaches here, so it never looks healthy. A wrong sibling clear likewise re-challenges, so its guard stays
    // set. Then, only for the tab that genuinely solved, tell sibling tabs: a durable hint for tabs that open
    // later, a live broadcast for open ones — hint before broadcast so a sibling reacting to the broadcast finds
    // a fresh hint.
    if (isCrossTabRecoveryGateOpen()) {
      wafChallengeManager.notifyClearanceValidated();
      wafCircuitBreaker.recordSuccess();
      if (thisTabGenuinelySolved) {
        writeClearedHint();
        broadcastCleared();
      }
    }
    if (attemptOutcome.kind === 'succeeded') {
      let durationMs: number | undefined;
      runGuarded(() => {
        const elapsedMs = Math.round(performance.now() - detectedAtMs);
        durationMs = Number.isFinite(elapsedMs) && elapsedMs >= 0 ? elapsedMs : undefined;
      });
      const sequenceId = recordWAFTelemetry({
        phase: WAFTelemetryPhase.Terminal,
        outcome: WAFTelemetryOutcomeExtra.Recovered,
        wafProvider: provider,
        durationMs,
        replayCount: replayCountWithThisAttempt,
        clearanceRole,
        transport,
        isLoginFlowRequest,
      });
      recordRecoveryVital({
        durationMs,
        replayCount: replayCountWithThisAttempt,
        sequenceId,
        clearanceRole,
        isLoginFlowRequest,
        transport,
      });
      return { kind: 'replayed', value: attemptOutcome.value };
    }
    // clearedButNotOk: the challenge cleared but the replay is not a success; hand it back, no Recovered.
    return { kind: 'replayed', value: attemptOutcome.value };
  }
}
